import { styled } from '@mui/material/styles';
import { css } from '@emotion/react';
import { NumberField as BaseNumberField } from '@base-ui/react/number-field';
import { InputAdornment } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';

// Outlined input with the same glassmorphism background as TextField's input
export const StyledOutlinedInput = styled(OutlinedInput)`
  border-radius: 8px;
  background: transparent;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);

  & .MuiOutlinedInput-input {
    color: ${({ theme }) => theme.palette.white['06']};
    font-weight: 100;
  }

  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  &.Mui-error .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${({ theme }) => theme.palette.status.offline};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${({ theme }) => theme.palette.primary['04']};
    background: transparent;
  }

  &.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline {
    border: 1px solid ${({ theme }) => theme.palette.status.offline};
  }
`;

// Wrapper that owns the relative positioning context for the floating label
export const Root = styled('div')`
  position: relative;
  width: 100%;
`;

interface GroupProps {
  error?: boolean;
}

// Outlined container for the input + stepper, mirrors .MuiOutlinedInput-root
export const Group = styled(BaseNumberField.Group, {
  shouldForwardProp: (prop) => prop !== 'error',
})<GroupProps>`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 40px;
  border-radius: 8px;
  background: transparent;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'light'
        ? theme.palette.grey['02']
        : 'transparent'};
  transition: border-color 0.2s ease;

  ${({ error, theme }) =>
    error &&
    css`
      border: 1px solid ${theme.palette.status.offline};
    `}

  &[data-focused] {
    border: 1px solid
      ${({ theme, error }) =>
        error ? theme.palette.status.offline : theme.palette.primary['04']};
  }

  &[data-disabled] {
    opacity: 0.6;
    pointer-events: none;
  }
`;

export const Input = styled(BaseNumberField.Input)`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 100;
  color: ${({ theme }) => theme.palette.white['06']};
  padding: 8.5px 14px;

  &::placeholder {
    color: ${({ theme }) => theme.palette.white['06']};
    opacity: 0.6;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
    box-shadow: 0 0 0px 1000px transparent inset !important;
    -webkit-text-fill-color: ${({ theme }) =>
      theme.palette.white['06']} !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

export const Adornment = styled(InputAdornment)`
  flex-direction: column;
  max-height: unset;
  align-self: stretch;
  border-left: 1px solid;
  border-color: ${({ theme }) => theme.palette.transparent.tertiary};
  margin-left: 24px;
  & button {
    padding-top: 0;
    padding-bottom: 0;
    flex: 1;
    border-radius: 4px;
  }
`;

export const Stepper = styled('div')`
  display: flex;
  flex-direction: column;
  border-left: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'light'
        ? theme.palette.grey['02']
        : theme.palette.transparent.tertiary};
`;

const stepperButtonStyles = ({
  theme,
}: {
  theme: import('@mui/material/styles').Theme;
}) => css`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 50%;
  cursor: pointer;
  color: ${theme.palette.white['06']};
  opacity: 0.7;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease;

  &:hover {
    opacity: 1;
    background-color: ${theme.palette.transparent.tertiary};
  }

  &:active {
    background-color: ${theme.palette.transparent.secondary};
  }

  &[data-disabled] {
    opacity: 0.25;
    cursor: not-allowed;
  }
`;

export const IncrementButton = styled(BaseNumberField.Increment)`
  ${stepperButtonStyles}
  border-bottom: 1px solid
    ${({ theme }) =>
    theme.palette.mode === 'light'
      ? theme.palette.grey['02']
      : theme.palette.transparent.tertiary};
`;

export const DecrementButton = styled(BaseNumberField.Decrement)`
  ${stepperButtonStyles}
`;

interface LabelProps {
  error?: boolean;
  shrink?: boolean;
}

// Floating label, mirrors the shrink/rest behaviour of .MuiInputLabel-root
export const Label = styled('label', {
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'shrink',
})<LabelProps>`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translate(4px, -50%) scale(1);
  transform-origin: top left;
  padding: 0 4px;
  pointer-events: none;
  background-color: transparent;
  color: ${({ theme }) => theme.palette.white['06']};
  font-size: 14px;
  font-weight: 400;
  transition: all 0.2s ease;
  max-width: calc(100% - 24px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ shrink, theme, error }) =>
    shrink &&
    css`
      top: 0;
      transform: translate(4px, -50%) scale(0.85);
      background-color: transparent;
      color: ${error
        ? theme.palette.status.offline
        : theme.palette.primary['02']};
    `}
`;
